/**
 * Close / Stop / Open vs the TRV dial.
 *
 * Cover-style wait: hold the commanded value and ignore status packets that
 * still report the previous reading. Commit when live matches, or restore
 * the previous value on timeout. Do not stall on “no progress” — BLE / viaq
 * often repeats the old `pos` for a long time before the new one arrives.
 *
 * Buttons: `pendingPosition` is the waiting state. Stop stays enabled while
 * opening/closing. The dial follows live `pos` / `target_C` and does not jump
 * to 0 / 100 when Open or Close is pressed.
 *
 * Slider: hold the user value until live matches or the wait times out.
 */

export const TRV_VALVE_SETTLED_DELTA = 0.5;
/** Hard cap waiting for the awaited pos / target_C after the command is queued. */
export const TRV_VALVE_SETTLE_TIMEOUT_MS = 120_000;
/** Unused on TRV (stallMs: 0). Repeated old readings are ignored, not treated as failure. */
export const TRV_VALVE_STALL_MS = 0;

export const isTrvValveSettled = (live: number | undefined, expected: number): boolean =>
	typeof live === 'number' && Math.abs(live - expected) < TRV_VALVE_SETTLED_DELTA;

export const liveMatchesTrvDialValue = (
	live: number | undefined,
	expected: number | undefined,
	threshold: number,
): boolean =>
	typeof live === 'number' && typeof expected === 'number' && Math.abs(live - expected) < threshold;

export type TrvSettlingOutcome = 'settled' | 'rpc_failed' | 'timeout' | 'stalled' | 'aborted';

export type TrvValveCommandStateInput = {
	currentPosition?: number;
	pendingPosition?: number;
	rpcPending?: boolean;
	disabled?: boolean;
};

export type TrvValveCommandState = {
	isClosed: boolean;
	isOpen: boolean;
	isOpening: boolean;
	isClosing: boolean;
	closeActive: boolean;
	openActive: boolean;
	stopActive: boolean;
	closeDisabled: boolean;
	openDisabled: boolean;
	stopDisabled: boolean;
	closeLoading: boolean;
	openLoading: boolean;
	stopLoading: boolean;
};

export const shouldClearTrvValvePending = (
	outcome: TrvSettlingOutcome | undefined,
	live: number | undefined,
	expected: number,
): boolean => {
	if (outcome === 'aborted' || outcome === 'rpc_failed' || outcome === 'stalled') return false;
	if (outcome === 'settled') return isTrvValveSettled(live, expected);
	return outcome === 'timeout';
};

/** Slider only: drop the hold and show live / previous again. */
export const shouldRevertTrvDialOptimistic = (outcome: TrvSettlingOutcome | undefined): boolean =>
	outcome === 'timeout';

export const resolveTrvValveCommandState = (input: TrvValveCommandStateInput): TrvValveCommandState => {
	const overlay = Boolean(input.disabled);
	const rpcPending = Boolean(input.rpcPending);
	const current = input.currentPosition;
	const pending = input.pendingPosition;

	const isClosed = isTrvValveSettled(current, 0);
	const isOpen = isTrvValveSettled(current, 100);
	const isClosing = pending === 0 && !isTrvValveSettled(current, 0);
	const isOpening = pending === 100 && !isTrvValveSettled(current, 100);
	const isMoving = isOpening || isClosing;

	return {
		isClosed,
		isOpen,
		isOpening,
		isClosing,
		closeActive: isClosing,
		openActive: isOpening,
		stopActive: rpcPending && !isMoving,
		closeDisabled: overlay || (isClosed && !isClosing),
		openDisabled: overlay || (isOpen && !isOpening),
		stopDisabled: overlay || (!isMoving && !rpcPending),
		closeLoading: rpcPending && isClosing,
		openLoading: rpcPending && isOpening,
		stopLoading: rpcPending && !isMoving,
	};
};
